import { ComponentType } from 'react';
import { hydrateRoot, createRoot, Root } from 'react-dom/client';
import Loadable from 'react-loadable';
import * as Sentry from '@sentry/react';
import 'core-js/stable';
import { BrowserHistoryBuildOptions } from 'history';
import 'regenerator-runtime/runtime';

import { Debug } from '@hh.ru/browser-api-utils';

import { RootReducer, AppRoutes, PageErrorState } from '../types';
import { kebabToCamelCase } from '../utils/notationConverter';
import AppWrapper from './app/App';
import { removeContainer, findReactRoot, saveContainer, saveReactRoot, removeReactRoot } from './modules/getRootNode';
import getOrCreateHistory from './modules/history';
import { getSentryClient } from './modules/initSentry';
import { setServiceName, getServiceName } from './modules/serviceName';
import makeStore from './modules/store';

let ProxyComponentMap: ComponentType | undefined;
let appReducer: RootReducer | null;
let appRoutes: AppRoutes | null;
let PageWrapper: ComponentType | undefined;
let ErrorComponent: ComponentType<PageErrorState> | undefined;
let _disableSpaMiddleware = false;
let _excludedSentryStoreKeys: string[] | undefined;
const root: Record<string, Root | undefined> = {};
let browserHistoryOptions: BrowserHistoryBuildOptions | undefined;

interface InitApp {
    unmountApp: boolean;
    container: HTMLDivElement;
    identifier: string;
    place: string;
}

type MakeInitApp = (prop: InitApp) => void;

const destroyApp = (identifier: string): void => {
    root[identifier]?.unmount?.();
    removeContainer(identifier);
    removeReactRoot(identifier);

    ProxyComponentMap = undefined;
    appReducer = null;
    appRoutes = null;
    PageWrapper = undefined;
    ErrorComponent = undefined;
    browserHistoryOptions = undefined;
    root[identifier] = undefined;
};

const initApp: MakeInitApp = ({ unmountApp, container, identifier, place }) => {
    // Проставляет правильный scope/client для redux интеграции и прочих сторонних вещей, связанных с Sentry
    // https://docs.sentry.io/platforms/javascript/best-practices/multiple-sentry-instances/
    Sentry.withScope(getSentryClient()?.scope, () => {
        if (unmountApp) {
            destroyApp(identifier);
            return;
        }
        saveContainer(container, identifier);
        const serviceName = getServiceName();
        const reactRoot = findReactRoot(identifier);

        if (!reactRoot) {
            Debug.log('error out', 'ReactRoot not found', {
                identifier,
                hasContainer: !!container,
                containerClassName: container?.classList,
                serviceName,
            });
            return;
        }

        saveReactRoot(reactRoot, identifier);

        if (!appReducer || !appRoutes) {
            Debug.log('error out', 'No passed parameters', {
                identifier,
                hasContainer: !!container,
                containerClassName: container?.classList,
                serviceName,
                hasReducer: !!appReducer,
                hasRoutes: !!appRoutes,
            });
            return;
        }

        const store = makeStore({
            identifier,
            appReducer,
            routes: appRoutes,
            disableSpaMiddleware: _disableSpaMiddleware,
            browserHistoryOptions,
            excludedSentryStoreKeys: _excludedSentryStoreKeys,
        });

        if (process.env.NODE_ENV === 'development' && store) {
            const storeInitialState = store.getState() as { [key: string]: unknown };

            import('@hh.ru/redux-dev-helper')
                .then(({ default: storeDevToolInit }) => storeDevToolInit(store, storeInitialState, serviceName, place))
                .catch(console.error);
        }

        if (store === null) {
            Debug.log('error out', 'Store has not been initialized', {
                identifier,
                hasContainer: !!container,
                containerClassName: container?.classList,
                serviceName,
            });

            return;
        }
        const history = getOrCreateHistory(browserHistoryOptions);

        const rootComponent = (
            <AppWrapper
                store={store}
                history={history}
                routes={appRoutes}
                proxyComponent={ProxyComponentMap}
                pageWrapper={PageWrapper}
                errorComponent={ErrorComponent}
                place={place}
            />
        );

        try {
            void Loadable.preloadReady().then(() => {
                if (reactRoot.hasChildNodes()) {
                    root[identifier] = hydrateRoot(reactRoot, rootComponent);
                } else {
                    root[identifier] = createRoot(reactRoot);
                    (root[identifier] as Root).render(rootComponent);
                }
                return undefined;
            });
        } catch (error) {
            Debug.log('error out', error);
            root[identifier] = createRoot(reactRoot);
            (root[identifier] as Root).render(rootComponent);
        }
    });
};

interface InitAppFactory {
    serviceName: string;
    reducer: RootReducer;
    routes: AppRoutes;
    proxyComponents: ComponentType;
    pageWrapper?: ComponentType;
    disableSpaMiddleware?: boolean;
    errorComponent?: ComponentType<PageErrorState>;
    historyOptions?: BrowserHistoryBuildOptions;
    excludedSentryStoreKeys?: string[];
}

export default ({
    serviceName,
    reducer,
    routes,
    proxyComponents,
    pageWrapper,
    disableSpaMiddleware,
    errorComponent,
    historyOptions,
    excludedSentryStoreKeys,
}: InitAppFactory): MakeInitApp => {
    ProxyComponentMap = proxyComponents;
    appReducer = reducer;
    appRoutes = routes;
    PageWrapper = pageWrapper;
    ErrorComponent = errorComponent;
    browserHistoryOptions = historyOptions;
    _disableSpaMiddleware = disableSpaMiddleware || false;
    _excludedSentryStoreKeys = excludedSentryStoreKeys || [];
    const serviceNameCamelCase = kebabToCamelCase(serviceName);

    setServiceName(serviceNameCamelCase);
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    __webpack_public_path__ = `${window.globalServiceVars[serviceNameCamelCase].staticHost}/`; // eslint-disable-line

    return initApp;
};
